import { authManager } from './auth/auth';
import { authState } from './auth/auth';
import { type ClientRequestOptions } from 'hono/client'
import { getEnvVariable } from '@lib/utils/getEnvVariable';
import { isNotAuthenticatedErrorCode } from '@lib/consts/authConsts';

// const PUBLIC_DOMAIN_NAME = getEnvVariable('PUBLIC_DOMAIN_NAME');
// const isMyOwnDomain = window.location.hostname === PUBLIC_DOMAIN_NAME || window.location.hostname.endsWith(`.${PUBLIC_DOMAIN_NAME}`);
// const isBrowserEnvironment = typeof window !== 'undefined';

const isRefreshing = (input: RequestInfo | URL, init?: RequestInit): boolean => {
    const refreshPath = `https://${getEnvVariable('PUBLIC_API_DOMAIN')}/auth_global/refresh`;
    return input.toString().includes(refreshPath) && init?.method === 'POST';
}
const isAuthorizing = (input: RequestInfo | URL, init?: RequestInit): boolean => {
    const authorizePath = `https://${getEnvVariable('PUBLIC_API_DOMAIN')}/auth/pkce/authorize`;
    return input.toString().includes(authorizePath) && init?.method === 'POST';
}
const isLoggingOut = (input: RequestInfo | URL, init?: RequestInit): boolean => {
    const logoutPath = `https://${getEnvVariable('PUBLIC_API_DOMAIN')}/auth_global/logout`;
    return input.toString().includes(logoutPath) && init?.method === 'POST';
}
const isLoggingOutAll = (input: RequestInfo | URL, init?: RequestInit): boolean => {
    const logoutAllPath = `https://${getEnvVariable('PUBLIC_API_DOMAIN')}/auth_global/logout-all`;
    return input.toString().includes(logoutAllPath) && init?.method === 'POST';
}
const isListingSessions = (input: RequestInfo | URL, init?: RequestInit): boolean => {
    const listSessionsPath = `https://${getEnvVariable('PUBLIC_API_DOMAIN')}/auth_global/list-sessions`;
    return input.toString().includes(listSessionsPath) && init?.method === 'POST';
}
const skipUnauthorizedRetry = (input: RequestInfo | URL, init?: RequestInit): boolean =>
    isRefreshing(input, init); // || isAuthorizing(input, init) || isLoggingOut(input, init) || isLoggingOutAll(input, init);

const isNotAuthenticatedResponse = async (response: Response): Promise<boolean> => {
    try {
        const json = await response.clone().json();
        return isNotAuthenticatedErrorCode(json?.error?.code);
    } catch {
        return false;
    }
};

export const getClientRequestOptions = (): ClientRequestOptions => {
    // if (!isBrowserEnvironment) {
    //     return {}
    // }
    return {
        // headers: {
        //     Authorization: 'Bearer TOKEN', // async not working here
        // },
        init: {
            credentials: 'include',
        },
        fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
            let accessToken = null;
            let refreshToken = null;
            // if (!isMyOwnDomain) {
            if (!isRefreshing(input, init)) {
                // authManager.getAccessToken also triggers a refresh if needed
                // so i should avoid triggering a new refresh while a refresh is in progress!
                accessToken = await authManager.getAccessToken();
            }

            if (
                isRefreshing(input, init) ||
                isAuthorizing(input, init) ||
                isLoggingOut(input, init) ||
                isLoggingOutAll(input, init) ||
                isListingSessions(input, init)
            ) {
                refreshToken = authState.value.refreshToken;
            }

            // old '...init?.headers,' was removing old headers

            // Note: init?.headers is a Headers object, spreading it doesn't work!
            // Must convert to plain object using Object.fromEntries
            const headersObj = init?.headers instanceof Headers
                ? Object.fromEntries(init.headers.entries())
                : init?.headers ?? {};

            const method = (init?.method ?? 'GET').toUpperCase();
            const isUnsafeMethod = method !== 'GET' && method !== 'HEAD';
            const hasContentType = Object.keys(headersObj).some(
                (key) => key.toLowerCase() === 'content-type'
            );
            // Hono RPC only sets Content-Type when `json:` is passed.
            // Empty `$post()` calls hit CSRF middleware as text/plain in NW.js.
            if (isUnsafeMethod && !hasContentType && !(init?.body instanceof FormData)) {
                (headersObj as any)['Content-Type'] = 'application/json';
            }

            const doFetch = (token: string | null) => fetch(input, {
                ...init,
                // credentials: 'include',
                headers: {
                    ...headersObj,
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    ...(refreshToken ? { 'x-refresh-token': refreshToken } : {}),
                },
            });

            let response = await doFetch(accessToken);
            if (
                accessToken &&
                !skipUnauthorizedRetry(input, init) &&
                await isNotAuthenticatedResponse(response)
            ) {
                const refreshed = await authManager.startRefreshingTokens();
                if (refreshed) {
                    response = await doFetch(authState.value.accessToken);
                }
            }

            return response;
        }
    }
}